/**
 * Service for managing antibody tables
 * Similar to figureService.js but for table management
 */

import { clerkService } from './clerkService';

const API_BASE_URL = import.meta.env?.VITE_API_URL || 'http://localhost:5001';

export class TableService {
    constructor() {
        this.cache = new Map();
        this.cacheTimestamps = new Map();
        this.CACHE_TTL = 60000; // 60 seconds cache

        // In-memory storage for tables (persists during session, clears on page refresh)
        this.sessionTables = [];
    }

    // Helper to get auth headers
    async _getAuthHeaders() {
        const token = await clerkService.getToken();
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }

    // Cache management
    _getCached(key) {
        const timestamp = this.cacheTimestamps.get(key);
        if (timestamp && Date.now() - timestamp < this.CACHE_TTL) {
            return this.cache.get(key);
        }
        return null;
    }

    _setCached(key, data) {
        this.cache.set(key, data);
        this.cacheTimestamps.set(key, Date.now());
    }

    clearCache() {
        this.cache.clear();
        this.cacheTimestamps.clear();
    }

    // Get all stored tables
    async fetchTables() {
        try {
            // Check cache first
            const cached = this._getCached('tables');
            if (cached) {
                return cached;
            }

            // For now, return from localStorage (in future, this would be a backend API)
            const tables = this.getStoredTables();
            
            // Cache the result
            this._setCached('tables', tables);
            
            return tables;
        } catch (error) {
            console.error('Error fetching tables:', error);
            // Return cached data if available, even if expired
            return this.cache.get('tables') || [];
        }
    }

    // Get a specific table by ID
    async getTable(tableId) {
        try {
            // Check cache first
            const cacheKey = `table_${tableId}`;
            const cached = this._getCached(cacheKey);
            if (cached) return cached;

            // Get from localStorage
            const tables = this.getStoredTables();
            const table = tables.find(t => t.id === tableId);
            
            if (table) {
                // Cache the result
                this._setCached(cacheKey, table);
            }
            
            return table;
        } catch (error) {
            console.error('Error fetching table:', error);
            return null;
        }
    }

    // Upload and parse antibody table from file
    async uploadTable(file, tableName) {
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const headers = await this._getAuthHeaders();
            const response = await fetch(`${API_BASE_URL}/api/antibody/upload`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
                headers
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to upload table');
            }
            
            const result = await response.json();
            
            // Store the table with a user-defined name and unique ID
            if (result.success) {
                const tableId = `table-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                const tableData = {
                    id: tableId,
                    name: tableName || result.filename.replace(/\.[^/.]+$/, ''), // Remove extension
                    filename: result.filename,
                    data: result.table_data,
                    specText: result.spec_text,
                    numRows: result.num_antibodies,
                    created: new Date().toISOString(),
                    modified: new Date().toISOString()
                };
                
                // Store in localStorage (in future, this would be saved to backend)
                this.saveTable(tableData);
                
                // Clear cache to force refresh
                this.clearCache();
                
                return tableData;
            }
            
            return result;
        } catch (error) {
            console.error('Error uploading table:', error);
            throw error;
        }
    }

    // Generate specification text from table data
    async generateSpec(tableData) {
        try {
            const headers = await this._getAuthHeaders();
            headers['Content-Type'] = 'application/json';
            
            const response = await fetch(`${API_BASE_URL}/api/antibody/generate-spec`, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: JSON.stringify({ table_data: tableData })
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to generate specification');
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error generating specification:', error);
            throw error;
        }
    }

    // Delete a table (session storage - persists during session, clears on refresh)
    async deleteTable(tableId) {
        // Remove from in-memory session storage
        this.sessionTables = this.sessionTables.filter(t => t.id !== tableId);

        // Clear cache
        this.clearCache();

        return true;
    }

    // Update table name (session storage - persists during session, clears on refresh)
    async updateTableName(tableId, newName) {
        // Update in in-memory session storage
        const table = this.sessionTables.find(t => t.id === tableId);
        if (table) {
            table.name = newName;
            table.modified = new Date().toISOString();
        }

        // Clear cache
        this.clearCache();

        return table || null;
    }

    // Helper methods (session storage only - clears on page refresh)
    getStoredTables() {
        // Return tables from in-memory session storage
        return this.sessionTables;
    }

    saveTable(tableData) {
        // Save to in-memory session storage (persists during session, clears on refresh)
        this.sessionTables.push(tableData);
        return true;
    }

    // Format table data for display in editor
    formatTableHTML(tableData) {
        let html = '<table class="antibody-table">';
        html += '<thead><tr>';
        html += '<th>Antibody Name</th>';
        html += '<th>VH</th><th>VL</th>';
        html += '<th>CDR-H1</th><th>CDR-H2</th><th>CDR-H3</th>';
        html += '<th>CDR-L1</th><th>CDR-L2</th><th>CDR-L3</th>';
        html += '</tr></thead>';
        html += '<tbody>';
        
        tableData.forEach(row => {
            html += '<tr>';
            html += `<td>${row.Ab_Name}</td>`;
            html += `<td>${row.VH ? `SEQ ID NO: ${row.VH}` : '-'}</td>`;
            html += `<td>${row.VL ? `SEQ ID NO: ${row.VL}` : '-'}</td>`;
            html += `<td>${row['CDR-H1'] ? `SEQ ID NO: ${row['CDR-H1']}` : '-'}</td>`;
            html += `<td>${row['CDR-H2'] ? `SEQ ID NO: ${row['CDR-H2']}` : '-'}</td>`;
            html += `<td>${row['CDR-H3'] ? `SEQ ID NO: ${row['CDR-H3']}` : '-'}</td>`;
            html += `<td>${row['CDR-L1'] ? `SEQ ID NO: ${row['CDR-L1']}` : '-'}</td>`;
            html += `<td>${row['CDR-L2'] ? `SEQ ID NO: ${row['CDR-L2']}` : '-'}</td>`;
            html += `<td>${row['CDR-L3'] ? `SEQ ID NO: ${row['CDR-L3']}` : '-'}</td>`;
            html += '</tr>';
        });
        
        html += '</tbody></table>';
        return html;
    }
}

// Create singleton instance
export const antibodyTableService = new TableService();